import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ProjectHealth } from '@/redux/service/project'
import {
  Activity,
  Clock,
  DollarSign,
  Target,
  BarChart3,
  Timer,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
} from 'lucide-react'
import { formatSeconds, formatCurrency, formatHours } from '@/lib/utils'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'

interface HealthModelProps {
  isOpen: boolean
  onClose: () => void
  project: ProjectHealth | null
  /** 'full' = finance role, 'mini' = mini_finance, 'none' = basic user */
  financeAccess?: 'full' | 'mini' | 'none'
}

/* ─────────────────── Animated progress bar ─────────────────── */
const ProgressBar = ({
  value,
  gradient,
  delay = 0,
}: {
  value: number
  gradient: string
  delay?: number
}) => (
  <div className='w-full h-1.5 bg-muted/30 rounded-full overflow-hidden'>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(value, 100)}%` }}
      transition={{ duration: 0.8, ease: 'easeOut', delay }}
      className={`h-full rounded-full ${gradient}`}
    />
  </div>
)

/* ─────────────────── Stacked bar (profit vs expense) ─────────────────── */
const StackedBar = ({
  segments,
}: {
  segments: { pct: number; gradient: string; label: string }[]
}) => (
  <div className='w-full h-2 bg-muted/30 rounded-full overflow-hidden flex'>
    {segments.map((s, i) => (
      <motion.div
        key={i}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(0, s.pct)}%` }}
        transition={{ duration: 0.8, ease: 'easeOut', delay: i * 0.15 }}
        className={`h-full ${s.gradient}`}
        title={s.label}
      />
    ))}
  </div>
)

const HealthModel: React.FC<HealthModelProps> = ({
  isOpen,
  onClose,
  project,
  financeAccess = 'none',
}) => {
  if (!project) return null

  const canSeeFinance = financeAccess === 'full' || financeAccess === 'mini'
  const isFullFinance = financeAccess === 'full'

  /* ── derived metrics ── */
  const completed = Number(project.completed_tasks)
  const total = Number(project.total_tasks)
  const taskPct = total > 0 ? (completed / total) * 100 : 0

  const capacity = Number(project.total_hours) || 0
  const billable = Number(project.billable_duration_seconds || 0) / 3600
  const nonBillable = Number(project.nonbillable_duration_seconds || 0) / 3600
  const totalTracked = billable + nonBillable
  const hoursLeft = capacity - totalTracked
  const isOverCapacity = hoursLeft < 0
  const capacityUsedPct =
    capacity > 0 ? Math.min(100, (totalTracked / capacity) * 100) : 0

  const start = project.start_date ? dayjs(project.start_date) : null
  const due = project.due_date ? dayjs(project.due_date) : null
  const today = dayjs()
  const totalDays = start && due ? due.diff(start, 'day') : 0
  const daysLeft = due ? due.diff(today, 'day') : 0
  const daysSpent = start ? today.diff(start, 'day') : 0
  const isOverdue = daysLeft < 0
  const timelinePct =
    totalDays > 0
      ? Math.min(100, Math.max(0, (daysSpent / totalDays) * 100))
      : 0

  const revenue = Number(project.billable_revenue) || 0
  const expenses =
    Number(project.total_employee_cost) ||
    revenue - (Number(project.profit) || 0)
  const profit = revenue - expenses
  const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0
  const expensePct = revenue > 0 ? (expenses / revenue) * 100 : 0
  const isProfit = profit >= 0

  const billablePct = totalTracked > 0 ? (billable / totalTracked) * 100 : 0

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-4xl w-[95vw] bg-background/95 backdrop-blur-xl border-border/40 shadow-2xl rounded-2xl overflow-hidden p-0'>
        {/* ── Header ── */}
        <DialogHeader className='px-8 pt-7 pb-5 border-b border-border/40'>
          <div className='flex items-start justify-between gap-4'>
            <div className='flex items-center gap-4 min-w-0'>
              <div className='p-2.5 bg-primary/10 rounded-xl shrink-0'>
                <Activity className='w-6 h-6 text-primary' />
              </div>
              <div className='min-w-0'>
                <DialogTitle className='text-xl font-bold tracking-tight truncate'>
                  {project.list_name}
                </DialogTitle>
                <div className='flex flex-wrap items-center gap-1.5 mt-1.5'>
                  <span className='px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-primary/8 text-primary border border-primary/15'>
                    {project.project_type}
                  </span>
                  {project.budget_type && (
                    <span className='px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-orange-500/8 text-orange-600 border border-orange-500/15'>
                      {project.budget_type}
                    </span>
                  )}
                  {project.schedule_status && (
                    <span className='px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-blue-500/8 text-blue-600 border border-blue-500/15'>
                      {project.schedule_status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* profit badge – top right – only for finance users */}
            {canSeeFinance && (
              <div
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold border ${
                  isProfit
                    ? 'bg-emerald-500/8 text-emerald-600 border-emerald-500/15'
                    : 'bg-destructive/8 text-destructive border-destructive/15'
                }`}
              >
                <div className='flex items-center gap-1.5'>
                  {isProfit ? (
                    <TrendingUp className='w-3.5 h-3.5' />
                  ) : (
                    <TrendingDown className='w-3.5 h-3.5' />
                  )}
                  {isProfit ? 'Profitable' : 'At Risk'}
                </div>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <div className='px-8 py-6 space-y-6 max-h-[72vh] overflow-y-auto custom-scrollbar'>
          {/* ── KPI Row ── */}
          <div
            className={`grid gap-3 ${canSeeFinance ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}
          >
            {/* Tasks */}
            <div className='space-y-2.5 p-4 rounded-xl bg-muted/15 border border-border/30'>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Target className='w-4 h-4 text-primary' />
                <span className='text-[11px] font-semibold uppercase tracking-wider'>
                  Tasks
                </span>
              </div>
              <p className='text-2xl font-bold tabular-nums'>
                {completed}
                <span className='text-sm font-normal text-muted-foreground'>
                  /{total}
                </span>
              </p>
              <ProgressBar
                value={taskPct}
                gradient='bg-gradient-to-r from-primary/70 to-primary'
              />
              <span className='text-[10px] font-medium text-muted-foreground/70'>
                {taskPct.toFixed(0)}% complete
              </span>
            </div>

            {/* Timeline */}
            <div className='space-y-2.5 p-4 rounded-xl bg-muted/15 border border-border/30'>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Timer className='w-4 h-4 text-blue-500' />
                <span className='text-[11px] font-semibold uppercase tracking-wider'>
                  Timeline
                </span>
              </div>
              <p
                className={`text-2xl font-bold tabular-nums ${isOverdue ? 'text-destructive' : ''}`}
              >
                {isOverdue ? `${Math.abs(daysLeft)}d` : `${daysLeft}d`}
                <span className='text-sm font-normal text-muted-foreground ml-1'>
                  {isOverdue ? 'overdue' : 'left'}
                </span>
              </p>
              <ProgressBar
                value={isOverdue ? 100 : timelinePct}
                gradient={
                  isOverdue
                    ? 'bg-gradient-to-r from-destructive/70 to-destructive'
                    : 'bg-gradient-to-r from-blue-400 to-blue-600'
                }
                delay={0.1}
              />
              <span className='text-[10px] font-medium text-muted-foreground/70'>
                {start ? start.format('MMM D') : '—'} →{' '}
                {due ? due.format('MMM D') : '—'}
              </span>
            </div>

            {/* Capacity */}
            <div className='space-y-2.5 p-4 rounded-xl bg-muted/15 border border-border/30'>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Clock className='w-4 h-4 text-emerald-500' />
                <span className='text-[11px] font-semibold uppercase tracking-wider'>
                  Hours
                </span>
              </div>
              <p
                className={`text-2xl font-bold tabular-nums ${isOverCapacity ? 'text-destructive' : ''}`}
              >
                {totalTracked.toFixed(1)}
                <span className='text-sm font-normal text-muted-foreground'>
                  /{formatHours(capacity)}h
                </span>
              </p>
              <ProgressBar
                value={capacityUsedPct}
                gradient={
                  isOverCapacity
                    ? 'bg-gradient-to-r from-destructive/70 to-destructive'
                    : 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                }
                delay={0.2}
              />
              <span className='text-[10px] font-medium text-muted-foreground/70'>
                {isOverCapacity
                  ? `${formatHours(Math.abs(hoursLeft))}h over`
                  : `${formatHours(hoursLeft)}h remaining`}
              </span>
            </div>

            {/* Budget – only for finance users */}
            {canSeeFinance && (
              <div className='space-y-2.5 p-4 rounded-xl bg-muted/15 border border-border/30'>
                <div className='flex items-center gap-2 text-muted-foreground'>
                  <DollarSign className='w-4 h-4 text-amber-500' />
                  <span className='text-[11px] font-semibold uppercase tracking-wider'>
                    Budget
                  </span>
                </div>
                <p className='text-2xl font-bold tabular-nums'>
                  {formatCurrency(project.total_budget)}
                </p>
                <div className='h-1.5' />
                <span className='text-[10px] font-medium text-muted-foreground/70'>
                  Total allocation
                </span>
              </div>
            )}
          </div>

          {/* ── Time Breakdown ── */}
          <div className='rounded-xl border border-border/30 bg-muted/8 overflow-hidden'>
            <div className='px-5 py-3.5 border-b border-border/20 flex items-center gap-2'>
              <Timer className='w-4 h-4 text-primary' />
              <h4 className='text-xs font-semibold uppercase tracking-wider text-foreground'>
                Time Breakdown
              </h4>
            </div>

            <div className='p-5'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
                {/* Total */}
                <div className='space-y-1'>
                  <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                    Total Tracked
                  </span>
                  <p className='text-2xl font-bold text-foreground tabular-nums'>
                    {totalTracked.toFixed(2)}h
                  </p>
                  <p className='text-[10px] text-muted-foreground/60'>
                    {formatSeconds(project.billable_duration_seconds)} billable
                    + {formatSeconds(project.nonbillable_duration_seconds)}{' '}
                    non‑billable
                  </p>
                </div>

                {/* Billable */}
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                      Billable
                    </span>
                    <span className='text-[10px] font-bold text-emerald-500 tabular-nums'>
                      {billablePct.toFixed(0)}%
                    </span>
                  </div>
                  <p className='text-lg font-bold tabular-nums'>
                    {billable.toFixed(2)}h
                  </p>
                  <ProgressBar
                    value={billablePct}
                    gradient='bg-gradient-to-r from-emerald-400 to-emerald-600'
                    delay={0.1}
                  />
                </div>

                {/* Non-Billable */}
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                      Non-Billable
                    </span>
                    <span className='text-[10px] font-bold text-orange-500 tabular-nums'>
                      {(100 - billablePct).toFixed(0)}%
                    </span>
                  </div>
                  <p className='text-lg font-bold tabular-nums'>
                    {nonBillable.toFixed(2)}h
                  </p>
                  <ProgressBar
                    value={100 - billablePct}
                    gradient='bg-gradient-to-r from-orange-400 to-orange-600'
                    delay={0.2}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Financials – only for finance users ── */}
          {canSeeFinance && (
            <div className='rounded-xl border border-border/30 bg-muted/8 overflow-hidden'>
              <div className='px-5 py-3.5 border-b border-border/20 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <BarChart3 className='w-4 h-4 text-primary' />
                  <h4 className='text-xs font-semibold uppercase tracking-wider text-foreground'>
                    Financial Summary
                  </h4>
                </div>
              </div>

              <div className='p-5 space-y-5'>
                <div
                  className={`grid gap-4 ${isFullFinance ? 'grid-cols-3' : 'grid-cols-2'}`}
                >
                  {/* Revenue */}
                  <div className='space-y-1'>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                      Revenue
                    </span>
                    <p className='text-xl font-bold tabular-nums'>
                      {formatCurrency(revenue)}
                    </p>
                  </div>

                  {/* Expenses – only for full finance */}
                  {isFullFinance && (
                    <div className='space-y-1'>
                      <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                        Expenses
                      </span>
                      <p className='text-xl font-bold text-orange-600 tabular-nums'>
                        {formatCurrency(expenses)}
                      </p>
                    </div>
                  )}

                  {/* Profit */}
                  <div className='space-y-1'>
                    <span className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                      {isFullFinance ? 'Net Profit' : 'Profit'}
                    </span>
                    <p
                      className={`text-xl font-bold tabular-nums ${isProfit ? 'text-emerald-500' : 'text-destructive'}`}
                    >
                      {formatCurrency(profit)}
                    </p>
                  </div>
                </div>

                {/* Margin bar – only for full finance */}
                {isFullFinance && (
                  <div className='space-y-2'>
                    <StackedBar
                      segments={[
                        {
                          pct: Math.max(0, marginPct),
                          gradient:
                            'bg-gradient-to-r from-emerald-400 to-emerald-600',
                          label: `Margin ${marginPct.toFixed(1)}%`,
                        },
                        {
                          pct: expensePct,
                          gradient:
                            'bg-gradient-to-r from-orange-400 to-orange-600',
                          label: `Expenses ${expensePct.toFixed(1)}%`,
                        },
                      ]}
                    />
                    <div className='flex items-center justify-between text-[10px] font-medium text-muted-foreground'>
                      <span className='flex items-center gap-1.5'>
                        <span className='w-2 h-2 rounded-full bg-emerald-500 inline-block' />
                        Margin {marginPct.toFixed(1)}%
                      </span>
                      <span className='flex items-center gap-1.5'>
                        <span className='w-2 h-2 rounded-full bg-orange-500 inline-block' />
                        Cost Ratio {expensePct.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Alert banner ── */}
          {nonBillable > billable && totalTracked > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className='flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/6 border border-destructive/15'
            >
              <AlertTriangle className='w-4 h-4 text-destructive shrink-0' />
              <p className='text-xs font-medium text-destructive'>
                Non-billable hours exceed billable output — review time
                allocation.
              </p>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default HealthModel
